<div class="sl-page animate-fade-in">

  <!-- ─── Header ─────────────────────────────────────────────────────────── -->
  <header class="sl-header">
    <div class="sl-header__title">
      <svg class="sl-header__icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
      </svg>
      <div>
        <h1 class="sl-header__headline">Sound Library</h1>
        <p class="sl-header__sub">{{ total() }} file{{ total() === 1 ? '' : 's' }}</p>
      </div>
    </div>

    <div class="sl-header__actions">
      <!-- Search -->
      <div class="sl-search">
        <svg class="sl-search__icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
        <input
          type="text"
          placeholder="Search by name or title…"
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearch()"
          class="sl-search__input"
          aria-label="Search audio files"
        />
      </div>

      <!-- Batch delete -->
      @if (selectedCount() > 0) {
        <button
          (click)="batchDelete()"
          [disabled]="isBatchDeleting()"
          class="sl-btn sl-btn--danger-ghost"
        >
          @if (isBatchDeleting()) {
            <svg class="sl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
          }
          Delete {{ selectedCount() }} selected
        </button>
      }

      <!-- Upload button -->
      <button (click)="openUploadPanel()" class="sl-btn sl-btn--create">
        <svg class="sl-btn__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Upload Sound
      </button>
    </div>
  </header>

  <!-- ─── Error banner ───────────────────────────────────────────────────── -->
  @if (errorMsg()) {
    <div class="sl-alert-error" role="alert">
      <span>{{ errorMsg() }}</span>
      <button (click)="errorMsg.set(null)" class="sl-alert-error__close" aria-label="Dismiss error">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
  }

  <!-- ─── Audio list ─────────────────────────────────────────────────────── -->
  <div class="sl-list-wrap">

    @if (isLoading() && audioFiles().length === 0) {
      <div class="sl-state-center">
        <svg class="sl-spinner sl-spinner--lg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Loading…
      </div>
    } @else if (audioFiles().length === 0) {
      <div class="sl-state-center sl-state-center--col">
        <svg class="sl-empty-icon" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z"/>
        </svg>
        <p class="sl-empty-title">No audio files yet</p>
        <p class="sl-empty-sub">Upload your first file to get started</p>
      </div>
    } @else {
      <!-- Select all bar -->
      <div class="sl-select-bar">
        <label class="sl-checkbox-label">
          <input
            type="checkbox"
            [checked]="allSelected()"
            (change)="toggleSelectAll()"
            class="sl-checkbox"
          />
          Select all
        </label>
        <span class="sl-select-bar__count">{{ total() }} total</span>
      </div>

      <!-- List -->
      <div class="sl-list">
        @for (file of audioFiles(); track file.id) {
          <div class="sl-item" [class.sl-item--selected]="isSelected(file.id)">
            <div class="sl-item__row">

              <!-- Checkbox -->
              <input
                type="checkbox"
                [checked]="isSelected(file.id)"
                (change)="toggleSelect(file.id)"
                class="sl-checkbox"
                [attr.aria-label]="'Select ' + file.title"
              />

              <!-- Play button -->
              <button
                (click)="togglePlay(file)"
                class="sl-play-btn"
                [attr.aria-label]="playingId() === file.id ? 'Pause ' + file.title : 'Play ' + file.title"
              >
                @if (playingId() === file.id) {
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                } @else {
                  <svg class="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8 5v14l11-7L8 5z"/>
                  </svg>
                }
              </button>

              <!-- Info + seek -->
              <div class="sl-item__info">
                <div class="sl-item__meta">
                  <span class="sl-item__title">{{ file.title }}</span>
                  <span class="sl-badge sl-badge--format">{{ mimeLabel(file.mime_type) }}</span>
                  @for (tag of file.tags; track tag) {
                    <span class="sl-badge sl-badge--tag">{{ tag }}</span>
                  }
                </div>

                <div class="sl-item__sub">
                  @if (playingId() === file.id) {
                    <span class="sl-time">{{ formatTime(currentTime()) }}</span>
                    <input
                      type="range"
                      [min]="0"
                      [max]="duration() || 1"
                      [value]="currentTime()"
                      (input)="seek($event)"
                      class="sl-seek"
                      aria-label="Seek"
                    />
                    <span class="sl-time sl-time--right">{{ formatTime(duration()) }}</span>
                  } @else {
                    <span class="sl-item__filename">{{ file.original_filename }}</span>
                    <span class="sl-dot">·</span>
                    <span class="sl-item__size">{{ formatSize(file.file_size) }}</span>
                  }
                </div>
              </div>

              <!-- Actions -->
              <div class="sl-item__actions">
                <button (click)="openEdit(file)" class="sl-icon-btn" aria-label="Edit">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
                <a
                  [href]="file.file_url"
                  download
                  class="sl-icon-btn"
                  aria-label="Download"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                  </svg>
                </a>
                <button (click)="openDelete(file)" class="sl-icon-btn sl-icon-btn--danger" aria-label="Delete">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Load more -->
      @if (hasMore()) {
        <div class="sl-load-more">
          <button
            (click)="loadMore()"
            [disabled]="isLoading()"
            class="sl-btn sl-btn--ghost"
          >
            @if (isLoading()) { Loading… } @else { Load more }
          </button>
        </div>
      }
    }
  </div>
</div>

<!-- ─── Upload Panel ───────────────────────────────────────────────────────── -->
@if (showUploadPanel()) {
  <div class="sl-modal-backdrop" [class.sl-modal-backdrop--closing]="uploadClosing()" (click)="closeUploadPanel()" role="dialog" aria-modal="true" aria-label="Upload Audio Files">
    <div class="sl-modal sl-modal--upload" [class.sl-modal--closing]="uploadClosing()" (click)="$event.stopPropagation()">

      <!-- Header -->
      <div class="sl-modal__header">
        <h2 class="sl-modal__title">Upload Audio Files</h2>
        <button
          (click)="closeUploadPanel()"
          [disabled]="isUploading()"
          class="sl-modal__close"
          aria-label="Close"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Drag & drop zone -->
      <div
        class="sl-dropzone"
        [class.sl-dropzone--over]="isDragOver()"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave()"
        (drop)="onDrop($event)"
        (click)="fileInput.click()"
        role="button"
        aria-label="Drop files here or click to select"
      >
        <svg class="sl-dropzone__icon" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z"/>
        </svg>
        <p class="sl-dropzone__label">Drop files here or <span class="sl-dropzone__link">browse</span></p>
        <p class="sl-dropzone__hint">MP3, WAV, OGG, FLAC, M4A, AAC, WMA — up to 50 MB each</p>
      </div>
      <input #fileInput type="file" multiple accept="audio/*,.mp3,.wav,.ogg,.flac,.m4a,.aac,.wma" class="hidden" (change)="onFileSelected($event)" />

      <!-- File queue -->
      @if (uploadItems().length > 0) {
        <div class="sl-modal__body">
          @for (item of uploadItems(); track item.file.name; let i = $index) {
            <div class="sl-upload-item"
              [class.sl-upload-item--uploading]="item.status === 'uploading'"
              [class.sl-upload-item--done]="item.status === 'done'"
              [class.sl-upload-item--error]="item.status === 'error'"
            >
              <!-- Status icon -->
              <div class="sl-upload-item__icon">
                @if (item.status === 'uploading') {
                  <svg class="sl-spinner text-[#506ef7]" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                } @else if (item.status === 'done') {
                  <svg class="w-5 h-5 text-[#86efac]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                  </svg>
                } @else if (item.status === 'error') {
                  <svg class="w-5 h-5 text-[#f87171]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                } @else {
                  <svg class="w-5 h-5 text-[var(--text-muted)]" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z"/>
                  </svg>
                }
              </div>

              <!-- Name + status -->
              <div class="sl-upload-item__info">
                <p class="sl-upload-item__name">{{ item.file.name }}</p>
                @if (item.status === 'error') {
                  <p class="sl-upload-item__status sl-upload-item__status--error">{{ item.error }}</p>
                } @else if (item.status === 'done') {
                  <p class="sl-upload-item__status sl-upload-item__status--done">Uploaded</p>
                } @else {
                  <p class="sl-upload-item__status">{{ formatSize(item.file.size) }}</p>
                }
              </div>

              <!-- Remove -->
              @if (item.status === 'pending' && !isUploading()) {
                <button (click)="removeUploadItem(i)" class="sl-icon-btn" aria-label="Remove">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              }
            </div>
          }
        </div>
      }

      <!-- Footer -->
      <div class="sl-modal__footer">
        <div class="sl-upload-stats">
          @if (uploadItems().length > 0) {
            {{ uploadDoneCount() }} done
            @if (uploadErrorCount() > 0) { · <span class="text-[#f87171]">{{ uploadErrorCount() }} failed</span> }
            @if (uploadPendingCount() > 0) { · {{ uploadPendingCount() }} pending }
          }
        </div>
        <div class="sl-modal__footer-actions">
          <button
            (click)="closeUploadPanel()"
            [disabled]="isUploading()"
            class="sl-btn sl-btn--ghost"
          >
            Close
          </button>
          <button
            (click)="startUpload()"
            [disabled]="isUploading() || uploadPendingCount() === 0"
            class="sl-btn sl-btn--primary"
          >
            @if (isUploading()) {
              <svg class="sl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Uploading…
            } @else {
              Upload {{ uploadPendingCount() > 0 ? uploadPendingCount() + ' file' + (uploadPendingCount() === 1 ? '' : 's') : '' }}
            }
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ─── Edit Modal ─────────────────────────────────────────────────────────── -->
@if (editingFile()) {
  <div class="sl-modal-backdrop" (click)="closeEdit()" role="dialog" aria-modal="true" aria-label="Edit Audio">
    <div class="sl-modal sl-modal--sm" (click)="$event.stopPropagation()">
      <div class="sl-modal__header">
        <h2 class="sl-modal__title">Edit Audio</h2>
        <button (click)="closeEdit()" [disabled]="isSavingEdit()" class="sl-modal__close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="sl-modal__body">
        @if (editError()) {
          <div class="sl-alert-error" role="alert"><span>{{ editError() }}</span></div>
        }
        <div class="sl-form-group">
          <label class="sl-form-label" for="edit-title">Title</label>
          <input
            id="edit-title"
            type="text"
            [(ngModel)]="editTitle"
            class="sl-form-input"
            placeholder="Audio title"
          />
        </div>
        <div class="sl-form-group">
          <label class="sl-form-label" for="edit-tags">Tags <span class="sl-form-label__hint">(comma-separated)</span></label>
          <input
            id="edit-tags"
            type="text"
            [(ngModel)]="editTags"
            class="sl-form-input"
            placeholder="e.g. music, background, intro"
          />
        </div>
      </div>
      <div class="sl-modal__footer">
        <div class="sl-modal__footer-actions">
          <button (click)="closeEdit()" [disabled]="isSavingEdit()" class="sl-btn sl-btn--ghost">Cancel</button>
          <button
            (click)="saveEdit()"
            [disabled]="isSavingEdit()"
            class="sl-btn sl-btn--primary"
          >
            @if (isSavingEdit()) {
              <svg class="sl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
            }
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- ─── Delete Confirm Modal ──────────────────────────────────────────────── -->
@if (fileToDelete()) {
  <div class="sl-modal-backdrop" (click)="closeDelete()" role="dialog" aria-modal="true" aria-label="Delete audio file">
    <div class="sl-modal sl-modal--sm" (click)="$event.stopPropagation()">
      <div class="sl-delete-body">
        <div class="sl-delete-icon">
          <svg class="w-6 h-6 text-[#f87171]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
          </svg>
        </div>
        <h3 class="sl-delete-title">Delete audio file?</h3>
        <p class="sl-delete-sub">
          "<span class="text-white font-medium">{{ fileToDelete()?.title }}</span>" will be permanently removed from the library and MinIO.
        </p>
      </div>
      <div class="sl-modal__footer sl-modal__footer--centered">
        <button (click)="closeDelete()" [disabled]="isDeleting()" class="sl-btn sl-btn--ghost sl-btn--flex">Cancel</button>
        <button
          (click)="confirmDelete()"
          [disabled]="isDeleting()"
          class="sl-btn sl-btn--danger sl-btn--flex"
        >
          @if (isDeleting()) {
            <svg class="sl-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          }
          Delete
        </button>
      </div>
    </div>
  </div>
}
